import { defineComponent as _defineComponent } from 'vue'
import { computed, watch, toRefs, ref } from "vue";
import Dialog from "@/components/vuetify-wrappers/NjDialog.vue";
import Select from "@/components/vuetify-wrappers/Select.vue";
import NjBtn from "@/components/vuetify-wrappers/NjButton.vue";
import { ApiService, LogService, SnackbarService } from "@/services";
import { useSessionStore } from "@/pinia/session";
import { useRoute } from "vue-router/composables";
import { getDefaultPptxCustomization } from "@/utils/PptxCustomizationUtils";


export default /*#__PURE__*/_defineComponent({
  __name: 'DossierDownloadDialog',
  props: {
    display: { type: Boolean },
    profileCount: null,
    checking: { type: Boolean },
    orderSelection: null,
    formatSelection: null,
    useCustomLogo: { type: Boolean },
    isSelectionActive: { type: Boolean, default: false },
    hasIneligibleItems: { type: Boolean }
  },
  emits: [
  "update:display",
  "update:order",
  "update:format",
  "update:logo",
  "update:customization",
],
  setup(__props: any, { emit }) {

const props = __props as { display: boolean, profileCount: number, checking: boolean, orderSelection: string, formatSelection: string, useCustomLogo: boolean, isSelectionActive: boolean, hasIneligibleItems?: boolean };

const route = useRoute();
const isBriefcasePage = computed(() => route.fullPath.includes("briefcase"));

const sessionStore = useSessionStore();
const hasCustomLogo = computed(() => sessionStore.hasCustomLogo);
const pcTemplates = computed(() => sessionStore.user?.pcTemplates ?? []);

// Props

const {
  isSelectionActive,
  hasIneligibleItems,
  orderSelection,
  formatSelection,
  useCustomLogo,
} = toRefs(props);



const downloadFormatOptions = [
  { id: "docx", name: "Microsoft Word (single document)" },
  { id: "zip_docx", name: "Microsoft Word (ZIP with separate files)" },
  { id: "pdf", name: "PDF (single document)" },
  { id: "zip_pdf", name: "PDF (ZIP with separate files)" },
  { id: "pptx", name: "PowerPoint (single document)" },
  { id: "zip_pptx", name: "PowerPoint (ZIP with separate files)" },
];

const sortOrderItems = [{ id: "alphabetical", name: "Alphabetical" }];
if (isBriefcasePage.value) {
  sortOrderItems.unshift({ id: "folderbriefcase_id", name: "As in briefcase" });
}
const filteredSortOrderItems = computed(() => {
  return isSelectionActive.value
    ? [{ id: "folderbriefcase_id", name: "As in briefcase" }]
    : sortOrderItems;
});

// Customization dropdown state and logic (replaces the old checkbox)
const customizationSelection = ref<string>("standard");

const isWordFormat = computed(
  () => formatSelection.value === "docx" || formatSelection.value === "zip_docx"
);
const isPdfFormat = computed(
  () => formatSelection.value === "pdf" || formatSelection.value === "zip_pdf"
);
const isPptxFormat = computed(
  () => formatSelection.value === "pptx" || formatSelection.value === "zip_pptx"
);

const customizationOptions = computed(() => {
  // PDF: Only Standard Template
  if (isPdfFormat.value) {
    return [{ id: "standard", name: "Standard Template" }];
  }
  // DOCX: Standard + Custom Logo
  if (isWordFormat.value) {
    return [
      { id: "standard", name: "Standard Template" },
      { id: "custom_logo", name: "Custom Logo" },
    ];
  }
  // PPTX: Standard + one option per custom branded template with prefix
  if (isPptxFormat.value) {
    const items: { id: string; name: string }[] = [
      { id: "standard", name: "Standard Template" },
    ];
    for (const t of pcTemplates.value) {
      items.push({
        id: `pc_template:${t.id}`,
        name: `Custom Template: ${t.title}`.trim(),
      });
    }
    return items;
  }
  return [{ id: "standard", name: "Standard Template" }];
});

const isCustomizationDisabled = computed(() => {
  if (isPdfFormat.value) return true;
  if (isPptxFormat.value) return pcTemplates.value.length === 0;
  return false;
});

function onCustomizationChanged(id: string) {
  customizationSelection.value = id;
  emit("update:logo", id === "custom_logo");
  emit("update:customization", id);
}

// Initialize defaults each time the dialog opens
watch(
  () => props.display,
  (val) => {
    if (val) {
      // Default should be Standard Template
      customizationSelection.value = "standard";
      // Apply dynamic rule for Word formats
      if (isWordFormat.value && hasCustomLogo.value) {
        customizationSelection.value = "custom_logo";
      } else if (isPptxFormat.value) {
        customizationSelection.value = getDefaultPptxCustomization(
          pcTemplates.value
        );
      }
      emit("update:logo", customizationSelection.value === "custom_logo");
      emit("update:customization", customizationSelection.value);
    }
  }
);

// Keep selection in sync when format or logo availability changes
watch([formatSelection, hasCustomLogo], () => {
  if (isPdfFormat.value) {
    customizationSelection.value = "standard";
  } else if (isWordFormat.value) {
    customizationSelection.value = hasCustomLogo.value
      ? "custom_logo"
      : "standard";
  } else if (isPptxFormat.value) {
    customizationSelection.value = getDefaultPptxCustomization(
      pcTemplates.value
    );
  }
  emit("update:logo", customizationSelection.value === "custom_logo");
  emit("update:customization", customizationSelection.value);
});

// If the available PPTX templates change while PPTX is selected (e.g., session loads)
watch(
  () => pcTemplates.value,
  () => {
    if (isPptxFormat.value) {
      customizationSelection.value = getDefaultPptxCustomization(
        pcTemplates.value
      );
      emit("update:customization", customizationSelection.value);
    }
  },
  { deep: true }
);

// Reflect parent-provided `useCustomLogo` prop into the dropdown when it changes
watch(useCustomLogo, (val) => {
  if (isWordFormat.value) {
    customizationSelection.value = val ? "custom_logo" : "standard";
  }
});

function requestCustomLogo() {
  ApiService.postUserContact("", true).then((response) => {
    if (response.statusCode === 200) {
      LogService.info(`Profile.requestCustomLogo is successful.`);
      SnackbarService.displaySnackbar(
        "Request for custom logo successfully submitted.",
        "primary"
      );
    } else {
      LogService.error(
        `Profile.requestCustomLogo failed: '${response.error_message}'`
      );
      SnackbarService.displaySnackbar("An error occurred, please try again.");
    }
  });
}

return { __sfc: true,route, isBriefcasePage, sessionStore, hasCustomLogo, pcTemplates, props, isSelectionActive, hasIneligibleItems, orderSelection, formatSelection, useCustomLogo, emit, downloadFormatOptions, sortOrderItems, filteredSortOrderItems, customizationSelection, isWordFormat, isPdfFormat, isPptxFormat, customizationOptions, isCustomizationDisabled, onCustomizationChanged, requestCustomLogo, Dialog, Select, NjBtn }
}

})